import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { UIProvider } from "@simurgh/feathers/provider/ui-provider";
import { Toaster } from "sonner";
import { RouterProvider as AriaRouterProvider } from "react-aria-components";

import { routeTree } from "./route-tree.gen";

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			refetchOnReconnect: false,
			staleTime: 1000 * 60 * 5, // 5 minutes
		},
		mutations: {
			retry: false,
		},
	},
});

const router = createRouter({
	routeTree,
	context: {
		queryClient,
	},
	defaultPreload: "intent",
	defaultPreloadStaleTime: 0,
});

function App() {
	return (
		<StrictMode>
			<QueryClientProvider client={queryClient}>
				<AriaRouterProvider navigate={(path) => router.navigate({ to: path })}>
					<UIProvider navigate={(path) => router.navigate({ to: path })}>
						<RouterProvider router={router} />
						<Toaster position="top-right" richColors closeButton />
						{import.meta.env.DEV && (
							<ReactQueryDevtools initialIsOpen={false} />
						)}
					</UIProvider>
				</AriaRouterProvider>
			</QueryClientProvider>
		</StrictMode>
	);
}

const rootElement = document.getElementById("root");
if (!rootElement) {
	throw new Error("Root element not found");
}

createRoot(rootElement).render(<App />);
